/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getEnvVarOptional, COSMOS_SITE_BASE_PATH } from "@cosmos/common-utils";

import { buildRobotsRules, buildRobotsTxt } from "./robots.render";

import type { RobotsTxtOptions } from "./robots.render";
import type { MetadataRoute } from "next";

// The rule builder and serializer live in the Workers-safe `robots.render` module so the
// epicgames edge worker can derive its group structure from the same function these sites use —
// see the note there. Re-exported here so app code has one import site.
export { buildRobotsRules, buildRobotsTxt, renderRobotsTxt } from "./robots.render";
export type {
	BuiltRobotsRule,
	RenderableRobots,
	RenderableRobotsRule,
	RobotsRulesOptions,
	RobotsTxtOptions,
} from "./robots.render";

/**
 * A single `User-agent` group. Next models `rules` as `Rule | Rule[]`; consumers that
 * need to walk the groups should use this
 * rather than re-narrowing the union at each call site.
 */
export type RobotsRule = Extract<MetadataRoute.Robots["rules"], readonly unknown[]>[number];

const baseRobotsRules: RobotsRule[] = buildRobotsRules();

// Lazily build the full site URL to avoid calling getEnvVarRequired at module evaluation time.
// This module can be pulled into client bundles via barrel exports, where EPIC_SERVER_URL is unavailable.
function getSitemapUrl(): string {
	return `${getEnvVarOptional("EPIC_SERVER_URL") ?? ""}${COSMOS_SITE_BASE_PATH()}/sitemap.xml`;
}

/**
 * Base robots config used by all websites.
 * Uses a getter for `sitemap` to defer URL resolution until access time,
 * avoiding module-level evaluation of server-only env vars in client bundles.
 *
 * `rules` is deliberately typed as `RobotsRule[]` rather than the looser
 * `MetadataRoute.Robots["rules"]` union: the array shape is part of the contract, so a
 * future change to it breaks consumers at compile time instead of silently at runtime.
 */
export const baseRobotsConfig: Omit<MetadataRoute.Robots, "rules"> & { rules: RobotsRule[] } = {
	rules: baseRobotsRules,
	get sitemap() {
		return getSitemapUrl();
	},
};

export function createRobotsConfig(additionalSitemaps: string[] = []): MetadataRoute.Robots {
	const sitemaps = [getSitemapUrl(), ...additionalSitemaps];

	return {
		rules: baseRobotsRules,
		sitemap: sitemaps.length === 1 ? sitemaps[0] : sitemaps,
	};
}

export default function robots(): MetadataRoute.Robots {
	return {
		rules: baseRobotsRules,
		sitemap: getSitemapUrl(),
	};
}

/**
 * Build a Next.js Route Handler (`GET`) that serves robots.txt as text, mirroring the thin
 * `createLlmsTxtRoute` wiring.
 *
 * Every Cosmos site uses this instead of Next's `app/robots.ts` metadata route, which cannot
 * serve the reservation: `resolveRobots` has fields for `User-Agent`, `Allow`, `Disallow`,
 * `Crawl-delay`, `other`, `Host` and `Sitemap` — and none for comments, so it cannot emit the
 * Content Signals preamble the reservation ships with.
 *
 * Lives at `app/robots.txt/route.ts`. A site must not also have `app/robots.ts`; two handlers for
 * the same path conflict.
 *
 *   export const GET = createRobotsTxtRoute({ reserveAiTraining: true });
 */
export function createRobotsTxtRoute(
	options: Omit<RobotsTxtOptions, "sitemap"> & { additionalSitemaps?: string[] } = {},
): () => Response {
	const { additionalSitemaps = [], ...rest } = options;

	return function GET(): Response {
		// Resolved per request, not at module scope: this module reaches client bundles via barrel
		// exports, where EPIC_SERVER_URL is unavailable.
		const body = buildRobotsTxt({ ...rest, sitemap: [getSitemapUrl(), ...additionalSitemaps] });

		return new Response(body, {
			headers: {
				"content-type": "text/plain; charset=utf-8",
				"cache-control": "public, max-age=3600",
			},
		});
	};
}
